// One line of a store's history or upcoming list: port of ActivityRow.swift (badge in the type or visit-result color,
// display title, Co-Pilot sparkle, Cancelled / Scheduled / Overdue chip, "From … to …", notes, duration, the visit
// verification pill, by-line and date, attachments) and InboundMessageRow ("Text received", New, subject, body).
// Photos and documents attached to an activity show as thumbnails right in the entry.

import { ArrowDownLeft, BadgeCheck, Building, Clock, Paperclip, Sparkles, TriangleAlert } from 'lucide-react';
import { memo } from 'react';
import { ACTIVITY_TYPE_TITLES, type DistanceUnit } from '@/contracts/enums';
import type { ActivityPayload, AttachmentPayload, CommPayload, UserPayload, VisitResultPayload } from '@/contracts/models';
import { parseWireDate } from '@/contracts/wireDate';
import type { DataItem, DataList } from '@/data/hooks';
import { formatShortDateTime } from '@/domain/format';
import { activityDisplayTitle } from '@/domain/records';
import { lookup } from '@/domain/util';
import { Chip, IconBadge, cn } from '@/ui';
import { ACTIVITY_ICONS } from './activityIcons';
import {
  ACTIVITY_TYPE_COLORS,
  activityByline,
  activityDateLine,
  activityStatusChip,
  activityTint,
  activityTransition,
  attachmentCountText,
  verificationBadge,
} from './detailModel';
import { DocumentRow, PhotoTile } from './FileTiles';
import { NewerAppBadge } from './NewerAppBadge';

export interface ActivityEntryProps {
  activity: ActivityPayload;
  /** The row (newer-app flag, last writer for "Logged from the office"). */
  item: DataItem<ActivityPayload> | undefined;
  users: ReadonlyMap<string, UserPayload>;
  visitResults: ReadonlyMap<string, VisitResultPayload>;
  attachments: DataList<AttachmentPayload>;
  distanceUnit: DistanceUnit;
  now: Date;
  onOpenPhoto: (item: DataItem<AttachmentPayload>) => void;
}

const PILL_TONE = {
  verified: 'bg-success-soft text-success-text',
  unverified: 'bg-warning-soft text-warning-text',
  office: 'bg-surface-3 text-fg-muted',
} as const;

export const ActivityEntry = memo(function ActivityEntry({
  activity: a,
  item,
  users,
  visitResults,
  attachments,
  distanceUnit,
  now,
  onOpenPhoto,
}: ActivityEntryProps) {
  const Icon = ACTIVITY_ICONS[a.type] ?? ACTIVITY_ICONS.note;
  const title = activityDisplayTitle(a, visitResults);
  const status = activityStatusChip(a, now);
  const transition = activityTransition(a);
  const pill = verificationBadge(a, distanceUnit, item?.modifiedBy);
  const files = a.attachmentIds
    .map((id) => attachments.get(id))
    .filter((f): f is DataItem<AttachmentPayload> => f !== undefined);
  const photos = files.filter((f) => f.value.kind === 'photo' || f.value.kind === 'businessCard');
  const documents = files.filter((f) => f.value.kind === 'document' || f.value.kind === 'audio');
  const overdue = status?.label === 'Overdue';

  return (
    <li className="flex gap-3 py-3">
      <IconBadge icon={<Icon />} color={activityTint(a, visitResults)} size={36} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 text-sm font-semibold text-fg">{title}</p>
          {a.isFromAI && (
            <span role="img" aria-label="Logged by Co-Pilot" title="Logged by Co-Pilot" className="inline-flex text-purple">
              <Sparkles aria-hidden className="size-3.5" />
            </span>
          )}
          {item?.undecodable !== undefined && <NewerAppBadge />}
          {status && <Chip color={status.color}>{status.label}</Chip>}
        </div>
        {transition && <p className="text-xs text-fg-muted">{transition}</p>}
        {a.notes.trim() !== '' && <p className="text-xs whitespace-pre-line break-words text-fg-muted">{a.notes}</p>}
        {a.durationMinutes !== undefined && a.durationMinutes > 0 && (
          <p className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
            <Clock aria-hidden className="size-3" />
            {a.durationMinutes} min
          </p>
        )}
        {pill && (
          <span
            className={cn(
              'inline-flex w-fit items-center gap-1 rounded-full px-2 py-1 text-[11px] leading-none font-semibold',
              PILL_TONE[pill.tone],
            )}
          >
            {pill.tone === 'verified' ? (
              <BadgeCheck aria-hidden className="size-3" />
            ) : pill.tone === 'unverified' ? (
              <TriangleAlert aria-hidden className="size-3" />
            ) : (
              <Building aria-hidden className="size-3" />
            )}
            {pill.text}
          </span>
        )}
        {photos.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {photos.map((p) => (
              <PhotoTile key={p.id} item={p} onOpen={onOpenPhoto} compact className="size-16" />
            ))}
          </div>
        )}
        {documents.length > 0 && (
          <ul className="divide-y divide-line">
            {documents.map((d) => (
              <DocumentRow key={d.id} item={d} />
            ))}
          </ul>
        )}
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-[11px] text-fg-muted">
          <span className="truncate">{activityByline(lookup(users, a.userId))}</span>
          <span className={cn('tabular-nums', overdue && 'font-semibold text-danger-text')}>{activityDateLine(a, now)}</span>
        </div>
        {a.attachmentIds.length > 0 && files.length < a.attachmentIds.length && (
          <p className="inline-flex items-center gap-1 text-[11px] text-fg-muted">
            <Paperclip aria-hidden className="size-3" />
            {attachmentCountText(a.attachmentIds.length)}
            {files.length === 0 ? '' : ` (${a.attachmentIds.length - files.length} not available)`}
          </p>
        )}
      </div>
    </li>
  );
});

/** A message that came in from the store (texts and emails received). */
export const MessageEntry = memo(function MessageEntry({
  comm: m,
  item,
  now,
}: {
  comm: CommPayload;
  item: DataItem<CommPayload> | undefined;
  now: Date;
}) {
  const channel = ACTIVITY_TYPE_TITLES[m.channel] ?? 'Message';
  const sent = parseWireDate(m.sentAt);
  return (
    <li className="flex gap-3 py-3">
      <IconBadge icon={<ArrowDownLeft />} color={ACTIVITY_TYPE_COLORS[m.channel] ?? '#009E9E'} size={36} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 text-sm font-semibold text-fg">{channel} received</p>
          {item?.undecodable !== undefined && <NewerAppBadge />}
          {!m.isRead && (
            <Chip color="#F96A3C" filled>
              New
            </Chip>
          )}
        </div>
        {m.subject.trim() !== '' && <p className="text-xs font-medium text-fg">{m.subject}</p>}
        {m.body.trim() !== '' && <p className="line-clamp-4 text-xs whitespace-pre-line break-words text-fg-muted">{m.body}</p>}
        {sent && <p className="text-[11px] text-fg-muted tabular-nums">{formatShortDateTime(sent, now)}</p>}
      </div>
    </li>
  );
});
